import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Key } from "react-aria-components";
import { css } from "../../../../../../styled-system/css";
import { Flex, HStack } from "../../../../../../styled-system/jsx";
import { useBrandStoreData } from "../../../../../modules/advertising/api/useBrandStoreData";
import { Select } from "../../../../fields/base/Select";
import { Button } from "../../../basic/Button";
import { FiExternalLink, FiX } from "react-icons/fi";

interface StorePageSelectorProps {
  brandEntityId?: string;
  categoryId?: number;
  storeId: string | null;
  pageUrl: string | null;
  onStoreChange: (storeId: string | null) => void;
  onPageChange: (pageUrl: string | null) => void;
  disableFields?: boolean;
  storeErrorMessage?: string;
  pageErrorMessage?: string;
}

export function StorePageSelector({
  brandEntityId,
  categoryId,
  storeId,
  pageUrl,
  onStoreChange,
  onPageChange,
  disableFields = false,
  storeErrorMessage,
  pageErrorMessage,
}: StorePageSelectorProps) {
  const { t } = useTranslation(["advertising"]);
  const { data, isLoading } = useQuery(
    useBrandStoreData({
      entityId: brandEntityId,
      categoryId,
    }),
  );

  const stores = data?.store_pages;

  const storeOptions = useMemo(
    () =>
      (stores ?? []).map((store) => ({
        value: store.store_name,
        label: store.store_name,
      })),
    [stores],
  );

  const pageOptions = useMemo(() => {
    if (storeId == null || !stores) return [];
    const selectedStore = stores.find((s) => s.store_name === storeId);
    if (!selectedStore) return [];
    return selectedStore.store_pages.map((page) => ({
      value: page.url,
      label: page.name,
    }));
  }, [stores, storeId]);

  const handleStoreChange = (key: Key | null) => {
    onStoreChange(typeof key === "string" ? key : null);
    onPageChange(null);
  };

  const handlePageChange = (key: Key | null) => {
    onPageChange(typeof key === "string" ? key : null);
  };

  return (
    <Flex gap="medium" className={css({ flexWrap: "wrap", alignItems: "flex-end" })}>
      <Select
        label={t(($) => $.advertising["Store"])}
        items={storeOptions}
        selectedKey={storeId}
        onSelectionChange={handleStoreChange}
        isDisabled={disableFields || isLoading || storeOptions.length === 0}
        placeholder={
          isLoading ? t(($) => $.advertising["Loading stores..."]) : t(($) => $.advertising["Select a store"])
        }
        css={{ flex: "1", minWidth: "200px" }}
        errorMessage={storeErrorMessage}
        popoverProps={{ placement: "bottom", shouldFlip: false }}
      />
      <Select
        label={t(($) => $.advertising["Store Page"])}
        items={pageOptions}
        selectedKey={pageUrl}
        onSelectionChange={handlePageChange}
        isDisabled={disableFields || storeId == null || pageOptions.length === 0}
        placeholder={
          storeId != null ? t(($) => $.advertising["Select a page"]) : t(($) => $.advertising["Select a store first"])
        }
        css={{ flex: "1", minWidth: "200px" }}
        errorMessage={pageErrorMessage}
        popoverProps={{ placement: "bottom", shouldFlip: false }}
      />
      {storeId != null && !disableFields && (
        <HStack className={css({ marginBottom: "medium" })}>
          {pageUrl && (
            <Button
              variant="contentOnly"
              tooltip={t(($) => $.advertising["Preview store page"])}
              onPress={() => window.open(pageUrl, "_blank", "noopener,noreferrer")}
              aria-label={t(($) => $.advertising["Preview store page"])}
              className={css({ color: "trellisPalette.0", _hover: { color: "trellisPalette.1" } })}
            >
              <FiExternalLink size={16} />
            </Button>
          )}
          <Button
            variant="contentOnly"
            tooltip={t(($) => $.advertising["Clear store and page selection"])}
            onPress={() => {
              onStoreChange(null);
              onPageChange(null);
            }}
            aria-label={t(($) => $.advertising["Clear store and page selection"])}
            className={css({ color: "gray.400", _hover: { color: "gray.600" } })}
          >
            <FiX size={16} />
          </Button>
        </HStack>
      )}
    </Flex>
  );
}
